import { useEffect, useRef, useState } from 'react';
import {
  changePlayerCategory,
  getCategoryChangeEligibility,
} from '@/api/tournament/categoryChangeApi';
import type { EloCategory } from '@/components/tournament/models/elo.model';
import { toast } from '@/components/ui/use-toast';
import type {
  CategoryChangeEligibility,
  Player,
} from '@/types/tournament.model';
import { handleGlobalError } from '@/utils/error-handler';

export interface CategoryChangeAction {
  eligible: boolean;
  /** Shown under the card's buttons when the blocked button is tapped. */
  blockedMessage?: string;
  onOpen: () => void;
}

interface TournamentCategoryChanges {
  tournamentId: string;
  /** By lowercased email. */
  eligibility: Map<string, CategoryChangeEligibility>;
  /**
   * Categories set from this page, by lowercased email: the tournament refetch that brings the
   * new ELO may not have landed yet, or may have failed silently.
   */
  movedTo: Map<string, EloCategory>;
}

const blockedMessageFor = (
  player: Player,
  entry: CategoryChangeEligibility,
): string => {
  switch (entry.reason) {
    case 'MANAGER_IS_THE_PLAYER':
      return 'No podés cambiar tu propia categoría.';
    case 'PLAYED_LATER_WITH_OTHER_MANAGER':
      return `${player.name} ya jugó un torneo de otro organizador después de este.`;
    case 'NO_ELO_IN_SPORT':
      return `${player.name} todavía no tiene categoría en este deporte.`;
    default:
      return 'No se puede cambiar la categoría de este jugador.';
  }
};

export function useCategoryChange(params: {
  tournamentId: string | undefined;
  /** True only for FINISHED tournaments whose card is open; nothing is fetched otherwise. */
  enabled: boolean;
  /** Refetches the tournament so the card's badge shows the new category. */
  onChanged: () => void | Promise<void>;
}): {
  actionFor: (player: Player) => CategoryChangeAction | undefined;
  open: boolean;
  /** Kept after closing so the sheet still has content while it slides out. */
  target: Player | null;
  /** The category this page last moved `target` to, if any. */
  movedTo: EloCategory | undefined;
  close: () => void;
  confirm: (category: EloCategory) => Promise<void>;
} {
  const { tournamentId, enabled, onChanged } = params;
  const [changes, setChanges] = useState<TournamentCategoryChanges | null>(
    null,
  );
  const [target, setTarget] = useState<Player | null>(null);
  const [open, setOpen] = useState(false);
  // The route component is reused across /torneos/:id: another tournament's data never applies.
  const current = changes?.tournamentId === tournamentId ? changes : null;

  // A change never alters who this manager can move, so this loads once per tournament and
  // reopening the card does not request it again. The ref also drops a late answer for a
  // tournament the page already left.
  const requestedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !tournamentId || requestedFor.current === tournamentId) {
      return;
    }
    requestedFor.current = tournamentId;
    const store = (items: CategoryChangeEligibility[]) => {
      if (requestedFor.current !== tournamentId) return;
      setChanges({
        tournamentId,
        eligibility: new Map(
          items.map((item) => [item.playerEmail.toLowerCase(), item]),
        ),
        movedTo: new Map(),
      });
    };
    getCategoryChangeEligibility(tournamentId)
      .then((items) => store(items ?? []))
      .catch((error) => {
        if (requestedFor.current !== tournamentId) return;
        store([]);
        handleGlobalError(
          error,
          'No se pudo cargar quién puede cambiar de categoría',
          'Recargá la página para reintentar.',
        );
      });
  }, [enabled, tournamentId]);

  const actionFor = (player: Player): CategoryChangeAction | undefined => {
    if (!enabled) return undefined;
    const entry = current?.eligibility.get(player.email.toLowerCase());
    if (!entry) return undefined;
    return {
      eligible: entry.eligible,
      blockedMessage: entry.eligible
        ? undefined
        : blockedMessageFor(player, entry),
      onOpen: () => {
        setTarget(player);
        setOpen(true);
      },
    };
  };

  const confirm = async (category: EloCategory) => {
    if (!target || !tournamentId) return;
    try {
      await changePlayerCategory(tournamentId, target.email, category.id);
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudo cambiar la categoría',
        'Probá de nuevo en unos minutos.',
      );
      throw error;
    }
    const email = target.email.toLowerCase();
    setChanges((prev) =>
      prev?.tournamentId === tournamentId
        ? { ...prev, movedTo: new Map(prev.movedTo).set(email, category) }
        : prev,
    );
    toast({ title: `${target.name} pasó a ${category.name}` });
    setOpen(false);
    await onChanged();
  };

  return {
    actionFor,
    open,
    target,
    movedTo: target
      ? current?.movedTo.get(target.email.toLowerCase())
      : undefined,
    close: () => setOpen(false),
    confirm,
  };
}
